// lane B2 (REQUEST-IDENTITY, contracts §1.3–1.4): the Accept · Revise · Reject card under a DE's
// delivery in chat. It sits under the last message of the run that delivered the request
// (`delivered_run_id`), and reads two ways:
//   - the company requires an explicit Accept (`done` false while delivered): "Waiting for your
//     Accept" — the request is not complete until a person accepts it;
//   - it does not (`done` true while delivered): "Delivered" — it already counts as done, and the
//     three actions are optional (nothing is gated on them).
// Accept and Reject go through `requests.decide` (Reject asks why; the reason is recorded).
// Revise is a reply to the delivery message, so the revision is keyed by its parent, never guessed;
// the server bounds it by the request's cap (2 writer rounds) and the card says how many are left.

import type { OperationOutput } from "@companyos/sdk";
import { Badge, Button, Input } from "@companyos/ui";
import { queryOptions, useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, PencilIcon, XIcon } from "lucide-react";
import { type FormEvent, useState } from "react";
import type { Api } from "../../api/client";
import { problemText } from "../settings/problem-text";
import { clientId } from "./chat-queries";
import type { ChatContext } from "./use-chat";

export type WorkRequest = OperationOutput<"requests.list">["data"][number];

export const requestsKey = (co: string, threadId: string) =>
  ["company", co, "chat", "requests", threadId] as const;

export const threadRequestsQuery = (api: Api, co: string, threadId: string) =>
  queryOptions({
    queryKey: requestsKey(co, threadId),
    queryFn: () => api.client.requests.list({ params: { co }, query: { thread_id: threadId } }),
    retry: false,
  });

/**
 * The requests of these threads that were delivered (or decided after a delivery), by the run
 * that delivered them: the card goes under that run's last message. Agents' threads only.
 */
export function useDeliveries(
  chat: ChatContext,
  threadIds: readonly string[],
): ReadonlyMap<string, WorkRequest> {
  const on = chat.agents && chat.meId !== null;
  const ids = [...new Set(threadIds.filter(Boolean))];
  const results = useQueries({
    queries: ids.map((id) => ({ ...threadRequestsQuery(chat.api, chat.co, id), enabled: on })),
  });
  const out = new Map<string, WorkRequest>();
  for (const r of results)
    for (const q of r.data?.data ?? [])
      if (q.delivered_run_id && !out.has(q.delivered_run_id)) out.set(q.delivered_run_id, q);
  return out;
}

/** The card's words for the request's state and the company's setting. */
export function deliveryCopy(
  r: Pick<WorkRequest, "state" | "done" | "revision" | "max_revisions">,
) {
  const left = Math.max(r.max_revisions - r.revision, 0);
  const revisions =
    left === 0
      ? "No revisions left: accept it or reject it."
      : `${left} revision${left === 1 ? "" : "s"} left.`;
  if (r.state === "accepted") return { title: "Accepted", text: null, gated: false, revisions };
  if (r.state === "rejected") return { title: "Rejected", text: null, gated: false, revisions };
  if (r.state !== "delivered") return null;
  return r.done
    ? {
        title: "Delivered",
        text: "This already counts as done. Accepting, asking for a revision or rejecting is optional.",
        gated: false,
        revisions,
      }
    : {
        title: "Waiting for your Accept",
        text: "This is not complete until you accept it.",
        gated: true,
        revisions,
      };
}

type Mode = "idle" | "revise" | "reject";

export function RequestDeliveryCard({
  chat,
  request,
  messageId,
  threadId,
}: {
  chat: ChatContext;
  request: WorkRequest;
  /** The delivery message (Revise replies to it). */
  messageId: string;
  threadId: string;
}) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>("idle");
  const [text, setText] = useState("");
  const copy = deliveryCopy(request);
  const refresh = () => qc.invalidateQueries({ queryKey: requestsKey(chat.co, threadId) });
  const decide = useMutation({
    mutationFn: (d: { decision: "accept" | "reject"; note?: string }) =>
      chat.api.client.requests.decide({
        params: { co: chat.co, id: request.id },
        body: d.note ? { decision: d.decision, note: d.note } : { decision: d.decision },
      }),
    onSuccess: () => setMode("idle"),
    onSettled: refresh,
  });
  const revise = useMutation({
    mutationFn: (note: string) =>
      chat.api.client.messages.create({
        params: { co: chat.co, id: threadId },
        body: {
          client_id: clientId(),
          body_markdown: note,
          parent_message_id: messageId,
        },
      }),
    onSuccess: () => {
      setMode("idle");
      setText("");
    },
    onSettled: refresh,
  });
  if (!copy) return null;
  const decided = request.state !== "delivered";
  const busy = decide.isPending || revise.isPending;
  const canRevise = request.revision < request.max_revisions;
  const readOnly = chat.readOnly || chat.meId === null;
  const error = decide.error ?? revise.error;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const note = text.trim();
    if (!note) return;
    if (mode === "reject") decide.mutate({ decision: "reject", note });
    else if (mode === "revise") revise.mutate(note);
  };
  return (
    <div
      data-slot="request-delivery-card"
      data-state={request.state}
      data-gated={copy.gated ? "true" : "false"}
      className="flex flex-col gap-1.5 rounded-md border bg-muted/30 px-3 py-2 text-sm"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={copy.gated ? "info" : "outline"}>{copy.title}</Badge>
        {request.revision > 0 ? (
          <span className="text-xs text-muted-foreground">Revision {request.revision}</span>
        ) : null}
      </div>
      {copy.text ? <p className="text-xs text-muted-foreground">{copy.text}</p> : null}
      {!decided && !readOnly ? (
        mode === "idle" ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="xs"
              variant={copy.gated ? "default" : "outline"}
              disabled={busy}
              onClick={() => decide.mutate({ decision: "accept" })}
              data-slot="request-accept"
            >
              <CheckIcon aria-hidden="true" />
              Accept
            </Button>
            <Button
              size="xs"
              variant="outline"
              disabled={busy || !canRevise}
              onClick={() => setMode("revise")}
              data-slot="request-revise"
            >
              <PencilIcon aria-hidden="true" />
              Revise
            </Button>
            <Button
              size="xs"
              variant="ghost"
              disabled={busy}
              onClick={() => setMode("reject")}
              data-slot="request-reject"
            >
              <XIcon aria-hidden="true" />
              Reject
            </Button>
            <span className="text-xs text-muted-foreground" data-slot="request-revisions">
              {copy.revisions}
            </span>
          </div>
        ) : (
          <form className="flex flex-wrap items-center gap-2" onSubmit={submit}>
            <Input
              aria-label={mode === "reject" ? "Why reject it" : "What should change"}
              className="h-7 w-64 text-xs"
              placeholder={mode === "reject" ? "Why reject it?" : "What should change?"}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <Button
              size="xs"
              type="submit"
              variant={mode === "reject" ? "destructive" : "default"}
              disabled={busy || text.trim().length === 0}
              data-slot={mode === "reject" ? "request-reject-confirm" : "request-revise-send"}
            >
              {mode === "reject" ? "Reject" : "Send revision"}
            </Button>
            <Button size="xs" type="button" variant="ghost" onClick={() => setMode("idle")}>
              Cancel
            </Button>
          </form>
        )
      ) : null}
      {error ? (
        <span role="alert" className="text-xs text-destructive">
          {problemText(error, "That did not go through.")}
        </span>
      ) : null}
    </div>
  );
}
